import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adApi } from '../../../api/ad.api';
import {
  RetentionType,
  DailyCohortRetentionResponse,
  DailyCohortRow,
  RetentionData,
  CohortUserItem,
  UserRetentionDetail,
} from '../../../types/Ad.api.type';
import { Card, CardContent, CardHeader } from '../../ui/card';
import { RefreshCw, User, X } from 'lucide-react';

/** 날짜 포맷 헬퍼: YYYY-MM-DD */
const formatDate = (date: Date): string => date.toISOString().slice(0, 10);

/** 기본 기간: 최근 14일 */
const getDefaultRange = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 14);
  return { from: formatDate(from), to: formatDate(to) };
};

export const RetentionDashboard = () => {
  const defaultRange = useMemo(getDefaultRange, []);
  const [from, setFrom] = useState(defaultRange.from);
  const [to, setTo] = useState(defaultRange.to);
  const [type, setType] = useState<RetentionType>('ALL');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  // 기존 DAU/WAU/MAU + 주간 코호트 (호환 유지)
  const { data: retention } = useQuery<RetentionData>({
    queryKey: ['analytics', 'retention'],
    queryFn: () => adApi.getRetention().then(res => res.data),
  });

  // 일별 코호트 리텐션
  const { data: cohortData, isLoading: loadingCohort } = useQuery<DailyCohortRetentionResponse>({
    queryKey: ['analytics', 'retention-cohort', from, to, type],
    queryFn: () => adApi.getRetentionCohort(from, to, type).then(res => res.data),
  });

  // 유저 상세 리텐션
  const { data: userDetail, isLoading: loadingUser } = useQuery<UserRetentionDetail>({
    queryKey: ['analytics', 'retention-user', selectedUserId],
    queryFn: () => adApi.getUserRetention(selectedUserId!).then(res => res.data),
    enabled: selectedUserId !== null,
  });

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center gap-2">
        <RefreshCw className="w-5 h-5 text-green-500" />
        <h3 className="text-lg font-bold text-gray-900">Retention (리텐션)</h3>
      </div>

      {/* DAU / WAU / MAU */}
      <div className="grid grid-cols-3 gap-4">
        <MetricCard label="DAU (일간 활성)" value={(retention?.activeUsers?.dau ?? 0).toLocaleString()} />
        <MetricCard label="WAU (주간 활성)" value={(retention?.activeUsers?.wau ?? 0).toLocaleString()} />
        <MetricCard label="MAU (월간 활성)" value={(retention?.activeUsers?.mau ?? 0).toLocaleString()} />
      </div>

      {/* 필터 바 */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 leading-none">기간</label>
              <input
                type="date"
                value={from}
                onChange={e => setFrom(e.target.value)}
                className="h-8 px-2 border rounded text-sm leading-none"
              />
              <span className="text-gray-400 leading-none">~</span>
              <input
                type="date"
                value={to}
                onChange={e => setTo(e.target.value)}
                className="h-8 px-2 border rounded text-sm leading-none"
              />
            </div>
            <div className="flex items-center gap-1">
              {(['ALL', 'LOGIN', 'ACTIVE'] as RetentionType[]).map(t => (
                <button
                  key={t}
                  onClick={() => setType(t)}
                  className={`h-8 px-3 rounded text-sm font-medium transition-colors ${
                    type === t
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t === 'ALL' ? '전체' : t === 'LOGIN' ? '접속' : '활성활동'}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 코호트 히트맵 */}
      <CohortHeatmap
        data={cohortData}
        loading={loadingCohort}
        onUserClick={setSelectedUserId}
      />

      {/* 유저 상세 모달 */}
      {selectedUserId !== null && (
        <UserRetentionModal
          detail={userDetail ?? null}
          loading={loadingUser}
          onClose={() => setSelectedUserId(null)}
        />
      )}
    </div>
  );
};

/** 숫자 메트릭 카드 */
const MetricCard = ({ label, value }: { label: string; value: string }) => (
  <Card>
    <div className="p-4">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="text-xl font-bold text-gray-900">{value}</p>
    </div>
  </Card>
);

/** 코호트 히트맵 테이블 */
const CohortHeatmap = ({
  data,
  loading,
  onUserClick,
}: {
  data: DailyCohortRetentionResponse | undefined;
  loading: boolean;
  onUserClick: (userId: number) => void;
}) => {
  const [expandedDate, setExpandedDate] = useState<string | null>(null);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-gray-500">
          코호트 데이터 로딩 중...
        </CardContent>
      </Card>
    );
  }

  const rows = data?.rows ?? [];
  if (rows.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-gray-500">
          해당 기간에 데이터가 없습니다.
        </CardContent>
      </Card>
    );
  }

  // 최대 dayOffset 계산
  const maxDay = rows.reduce((max, row) => {
    const keys = Object.keys(row.retention).map(Number);
    return Math.max(max, ...keys);
  }, 0);

  const dayColumns = Array.from({ length: Math.min(maxDay, 30) }, (_, i) => i + 1);

  return (
    <Card>
      <CardHeader className="pb-2">
        <h4 className="text-sm font-semibold text-gray-700">일별 코호트 리텐션 히트맵</h4>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[800px]">
            <thead className="bg-gray-50 border-b sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">가입일</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600 whitespace-nowrap">크기</th>
                {dayColumns.map(d => (
                  <th key={d} className="px-2 py-2 text-center font-medium text-gray-600 whitespace-nowrap">
                    D{d}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.map(row => (
                <CohortRow
                  key={row.cohortDate}
                  row={row}
                  dayColumns={dayColumns}
                  expanded={expandedDate === row.cohortDate}
                  onToggle={() =>
                    setExpandedDate(expandedDate === row.cohortDate ? null : row.cohortDate)
                  }
                  onUserClick={onUserClick}
                />
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

/** 코호트 행 */
const CohortRow = ({
  row,
  dayColumns,
  expanded,
  onToggle,
  onUserClick,
}: {
  row: DailyCohortRow;
  dayColumns: number[];
  expanded: boolean;
  onToggle: () => void;
  onUserClick: (userId: number) => void;
}) => {
  const { data: cohortUsers, isLoading: loadingUsers } = useQuery<CohortUserItem[]>({
    queryKey: ['analytics', 'cohort-users', row.cohortDate],
    queryFn: () => adApi.getCohortUsers(row.cohortDate).then(res => res.data),
    enabled: expanded,
  });

  return (
    <>
      <tr
        className="hover:bg-gray-50 cursor-pointer"
        onClick={onToggle}
      >
        <td className="px-3 py-2 font-medium whitespace-nowrap">{row.cohortDate}</td>
        <td className="px-3 py-2 text-right">{row.cohortSize}</td>
        {dayColumns.map(d => (
          <td key={d} className="px-1 py-2 text-center">
            <RetentionCell value={row.retention[d] ?? null} />
          </td>
        ))}
      </tr>
      {expanded && (
        <tr>
          <td colSpan={dayColumns.length + 2} className="bg-gray-50 px-4 py-3">
            <p className="text-xs text-gray-500 mb-2">
              {row.cohortDate} 가입 코호트 ({row.cohortSize}명) — 유저를 클릭하면 리텐션 상세를 볼 수 있어요
            </p>
            {loadingUsers ? (
              <p className="text-xs text-gray-400">유저 목록 로딩 중...</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {cohortUsers?.map(u => {
                  const isCherryPicker = u.activeDays <= 1;
                  return (
                    <button
                      key={u.userId}
                      onClick={e => { e.stopPropagation(); onUserClick(u.userId); }}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs transition-colors ${
                        isCherryPicker
                          ? 'bg-red-50 border border-red-200 text-red-600 hover:bg-red-100'
                          : 'bg-white border border-gray-200 text-gray-700 hover:bg-blue-50 hover:border-blue-300'
                      }`}
                    >
                      <User className={`w-3 h-3 ${isCherryPicker ? 'text-red-400' : 'text-gray-400'}`} />
                      {u.nickname}
                    </button>
                  );
                })}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

/** 리텐션 셀 - 히트맵 색상 */
const RetentionCell = ({ value }: { value: number | null }) => {
  if (value === null) return <span className="text-gray-200">-</span>;

  const bg = getHeatmapColor(value);
  return (
    <span className={`inline-block w-full px-1 py-0.5 rounded text-[11px] font-medium ${bg}`}>
      {value.toFixed(1)}%
    </span>
  );
};

/** 히트맵 색상 결정 */
const getHeatmapColor = (percent: number): string => {
  if (percent >= 50) return 'bg-green-200 text-green-800';
  if (percent >= 30) return 'bg-green-100 text-green-700';
  if (percent >= 20) return 'bg-yellow-100 text-yellow-700';
  if (percent >= 10) return 'bg-orange-100 text-orange-700';
  if (percent > 0) return 'bg-red-100 text-red-600';
  return 'bg-gray-100 text-gray-400';
};

/** 유저 리텐션 상세 모달 */
const UserRetentionModal = ({
  detail,
  loading,
  onClose,
}: {
  detail: UserRetentionDetail | null;
  loading: boolean;
  onClose: () => void;
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
    <div
      className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-auto mx-4"
      onClick={e => e.stopPropagation()}
    >
      {/* 헤더 */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-gray-500" />
          <h4 className="font-semibold text-gray-900">
            {loading ? '로딩 중...' : `${detail?.nickname ?? '-'} (ID: ${detail?.userId ?? '-'})`}
          </h4>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* 내용 */}
      <div className="p-4">
        {loading && <p className="text-center text-gray-500 py-8">로딩 중...</p>}
        {!loading && !detail && <p className="text-center text-gray-500 py-8">유저를 찾을 수 없습니다.</p>}
        {!loading && detail && (
          <>
            <p className="text-sm text-gray-600 mb-3">
              가입일: {detail.registrationDate}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Day</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">날짜</th>
                    <th className="px-3 py-2 text-center font-medium text-gray-600">접속</th>
                    <th className="px-3 py-2 text-center font-medium text-gray-600">투표</th>
                    <th className="px-3 py-2 text-center font-medium text-gray-600">출석</th>
                    <th className="px-3 py-2 text-center font-medium text-gray-600">활성</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {detail.days.map(day => (
                    <tr key={day.dayOffset} className="hover:bg-gray-50">
                      <td className="px-3 py-1.5 font-medium">D{day.dayOffset}</td>
                      <td className="px-3 py-1.5 text-gray-600">{day.date}</td>
                      <td className="px-3 py-1.5 text-center">
                        <ActivityDot active={day.loggedIn} />
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        {day.voteCount > 0 ? (
                          <span className="text-blue-600 font-medium">{day.voteCount}</span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        <ActivityDot active={day.attended} />
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        <ActivityDot active={day.active} color="green" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  </div>
);

/** 활동 표시 점 */
const ActivityDot = ({ active, color = 'blue' }: { active: boolean; color?: 'blue' | 'green' }) => {
  if (!active) return <span className="text-gray-300">-</span>;
  const dotColor = color === 'green' ? 'bg-green-500' : 'bg-blue-500';
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${dotColor}`} />;
};
